/**
 * Chat Message Utilities
 * 
 * Features:
 * - Message filtering and validation for display
 * - Message sorting by timestamp with error handling
 * - Message formatting for clipboard export
 * - Message content validation and sanitization
 * 
 * Logic:
 * - Filters out invalid messages (missing role/timestamp)
 * - Sorts messages chronologically with graceful error handling
 * - Formats messages for clipboard with user/AI labels and timestamps
 * - Validates message structure and content
 * 
 * Changes:
 * - Extracted from Chat.tsx component for better separation of concerns
 * - Added comprehensive error handling for malformed messages
 * - Created reusable message processing functions
 */

import { ChatMessage } from '../../models/ChatMessage';

/**
 * Filters and validates messages for display
 * @param messages Array of chat messages
 * @returns Filtered array of valid messages
 */
export const displayMessages = (messages: ChatMessage[]): ChatMessage[] => {
  return messages.filter(message => {
    if (!message?.role) return false;
    return true;
  });
};

/**
 * Sorts messages by timestamp with error handling
 * @param messages Array of chat messages
 * @returns Sorted array of messages
 */
export const sortMessagesByTimestamp = (messages: ChatMessage[]): ChatMessage[] => {
  return messages
    .filter(m => m && m.timestamp) // Filter out invalid messages
    .sort((a, b) => {
      try {
        return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
      } catch (error) {
        console.warn('Error sorting messages by timestamp:', error, a, b);
        return 0; // Keep original order if timestamps are invalid
      }
    });
};

/**
 * Filters messages that can be copied to clipboard
 * @param messages Array of chat messages
 * @returns Array of user and assistant messages with content
 */
export const getCopiableMessages = (messages: ChatMessage[]): ChatMessage[] => {
  return messages.filter(msg =>
    msg.role === 'user' || (msg.role === 'assistant' && msg.content?.trim())
  );
};

/**
 * Formats messages for clipboard export
 * @param messages Array of chat messages
 * @returns Formatted string for clipboard
 */
export const formatMessagesForClipboard = (messages: ChatMessage[]): string => {
  const copiableMessages = getCopiableMessages(messages);

  if (copiableMessages.length === 0) {
    return '';
  }

  return copiableMessages
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
    .map(msg => {
      const role = msg.role === 'user' ? 'You' : 'AI';
      const content = msg.content || '';
      const date = new Date(msg.timestamp);
      const dateTime = date.toLocaleString();
      return `**${role}** (${dateTime}):\n${content}`;
    })
    .join('\n\n---\n\n');
};

/**
 * Validates message content
 * @param content Message content to validate
 * @returns True if content is valid (non-empty after trim)
 */
export const isValidMessageContent = (content: string): boolean => {
  return content?.trim().length > 0;
};

/**
 * Validates message structure
 * @param message Message object to validate
 * @returns True if message has required fields
 */
export const isValidMessage = (message: any): message is ChatMessage => {
  return message &&
    typeof message.role === 'string' &&
    typeof message.timestamp === 'string' &&
    message.id !== undefined;
};

/**
 * Gets the count of messages that can be copied
 * @param messages Array of chat messages
 * @returns Number of copiable messages
 */
export const getCopiableMessageCount = (messages: ChatMessage[]): number => {
  return getCopiableMessages(messages).length;
};
